import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X, AlertTriangle } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * Guarda contra el cierre accidental de un modal con trabajo sin guardar.
 *
 * Motivo: Radix cierra el diálogo con Escape y con un clic fuera. En un formulario
 * de 30-45 campos (crear predio, editar predio, rectificación) eso borra todo sin
 * decir nada. Sólo 2 de los 69 modales con captura bloqueaban el clic fuera, y
 * NINGUNO el teclado: el modal de crear predio, por ejemplo, tenía cubierto el
 * mouse (`onInteractOutside`) pero no la tecla, que va por otra vía.
 *
 * Cómo funciona: el contenido se marca "sucio" en cuanto el usuario escribe o elige
 * algo dentro. Si está limpio, Escape y clic fuera cierran como siempre (sin fricción
 * en visores y confirmaciones). Si está sucio, se interceptan y se pide confirmación
 * dentro del propio modal.
 *
 * `sinGuardaDeCambios`: para modales cuyo único input es un buscador o un filtro,
 * donde teclear no es "trabajo" que se pueda perder.
 *
 * El cierre programático (tras guardar) NO pasa por aquí, así que no se ve afectado.
 * El botón "Descartar" es un Close real de Radix para que `onOpenChange` siga
 * disparándose y los autoguardados que dependen de él sigan corriendo.
 */
const DialogContent = React.forwardRef(({
  className,
  children,
  sinGuardaDeCambios = false,
  onEscapeKeyDown,
  onInteractOutside,
  ...props
}, ref) => {
  const [sucio, setSucio] = React.useState(false);
  const [confirmando, setConfirmando] = React.useState(false);
  const contenidoRef = React.useRef(null);

  // Combinar el ref externo con el interno que necesitamos para los listeners.
  const asignarRef = React.useCallback((nodo) => {
    contenidoRef.current = nodo;
    if (typeof ref === 'function') ref(nodo);
    else if (ref) ref.current = nodo;
  }, [ref]);

  React.useEffect(() => {
    if (sinGuardaDeCambios) return;
    const nodo = contenidoRef.current;
    if (!nodo) return;
    // Un campo de BÚSQUEDA no es trabajo que se pueda perder. Casi todos los modales
    // de mutación traen un buscador de predios; sin esta excepción, teclear ahí y
    // cerrar pediría confirmación sin que hubiera nada que proteger.
    const esBusqueda = (el) => {
      if (!el || el.tagName !== 'INPUT') return false;
      if (el.type === 'search') return true;
      const pista = `${el.placeholder || ''} ${el.getAttribute('aria-label') || ''} ${el.name || ''}`.toLowerCase();
      return /busc|search|filtr/.test(pista);
    };
    const marcar = (e) => {
      // Un campo puede declararse irrelevante para la guarda (buscadores dentro de
      // un formulario que por lo demás sí tiene trabajo que proteger).
      if (e.target?.closest?.('[data-sin-guarda]')) return;
      if (esBusqueda(e.target)) return;
      setSucio(true);
    };
    // `input`/`change` cubren texto, número, textarea, checkbox y selects nativos.
    // Los Select de Radix no son <select>, así que además se escucha la elección
    // de una opción.
    const marcarOpcion = (e) => {
      if (e.target?.closest?.('[role="option"]')) marcar(e);
    };
    nodo.addEventListener('input', marcar, true);
    nodo.addEventListener('change', marcar, true);
    nodo.addEventListener('click', marcarOpcion, true);
    return () => {
      nodo.removeEventListener('input', marcar, true);
      nodo.removeEventListener('change', marcar, true);
      nodo.removeEventListener('click', marcarOpcion, true);
    };
  }, [sinGuardaDeCambios]);

  const interceptar = (e, handlerOriginal) => {
    handlerOriginal?.(e);
    if (e.defaultPrevented) return;          // el modal ya decidió bloquearlo por su cuenta
    if (sinGuardaDeCambios || !sucio) return; // nada que perder: cerrar normal
    e.preventDefault();
    setConfirmando(true);
  };

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={asignarRef}
        onEscapeKeyDown={(e) => interceptar(e, onEscapeKeyDown)}
        onInteractOutside={(e) => interceptar(e, onInteractOutside)}
        className={cn(
        // [&>*]:min-w-0 — las celdas de un grid traen min-width:auto, así que un hijo
        // ancho (típicamente el DialogFooter con varios botones whitespace-nowrap)
        // ensanchaba la pista y arrastraba a los demás hijos fuera del panel: los
        // campos se dibujaban por fuera del fondo blanco.
        "fixed left-[50%] top-[50%] z-50 grid w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-4 sm:p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg overflow-visible [&>*]:min-w-0",
        className
      )}
        {...props}>
        {children}
        <DialogPrimitive.Close
          onClick={(e) => {
            // La X también debe respetar la guarda: cerrar por el aspa pierde
            // exactamente lo mismo que cerrar con Escape.
            if (!sinGuardaDeCambios && sucio && !confirmando) {
              e.preventDefault();
              setConfirmando(true);
            }
          }}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-10">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>

        {confirmando && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-slate-900/40 p-4"
            role="alertdialog"
            aria-modal="true"
            aria-label="Cambios sin guardar"
          >
            <div className="w-full max-w-sm rounded-lg border border-amber-300 bg-white p-4 shadow-xl">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900">Tiene cambios sin guardar</p>
                  <p className="mt-1 text-sm text-slate-600">
                    Si cierra ahora, se pierde lo que lleva diligenciado en este formulario.
                  </p>
                </div>
              </div>
              <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <DialogPrimitive.Close asChild>
                  <button
                    type="button"
                    className="rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                  >
                    Descartar cambios
                  </button>
                </DialogPrimitive.Close>
                <button
                  type="button"
                  autoFocus
                  onClick={() => setConfirmando(false)}
                  className="rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                >
                  Seguir editando
                </button>
              </div>
            </div>
          </div>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  // gap-2 en vez de sm:space-x-2: con space-x-* los botones que envuelven a una
  // segunda línea quedan sin separación vertical. sm:flex-wrap deja que envuelvan
  // en vez de forzar el ancho del diálogo (ver [&>*]:min-w-0 en DialogContent).
  <div
    className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
